import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "../../hooks/useMeta";
import { LandingHeader, LandingFooter } from "../../components/landing";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faLightbulb,
  faRocket,
  faBullseye,
  faEye,
  faSeedling,
} from "@fortawesome/pro-solid-svg-icons";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";
import Collapse from "../../components/utils/Collapse";
import UserAvatar from "../../components/utils/UserAvatar";

// Co-founder images - placeholder paths, user will need to add actual images
import founder1Img from "../../assets/img/landing/reworked/about/founder-1.webp";
import founder2Img from "../../assets/img/landing/reworked/about/founder-2.webp";
import founder3Img from "../../assets/img/landing/reworked/about/founder-3.webp";
import founder4Img from "../../assets/img/landing/reworked/about/founder-4.webp";
import founder5Img from "../../assets/img/landing/reworked/about/founder-5.webp";

function LandingAbout() {
  const { t } = useTranslation();
  usePageTitle("landingAbout");

  // Parallax scroll effect
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Accordion state - only one open at a time, Histoire (index 0) open by default
  const [openAccordionIndex, setOpenAccordionIndex] = useState(0);

  // Selected founder for team section (hover/click)
  const [selectedFounderIndex, setSelectedFounderIndex] = useState(null);

  // Window width for responsive radius calculation
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Helper function to get initials from name
  const getInitials = useCallback((name) => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }, []);

  // Co-founders data - ordered as 5-2-1-4-3
  const founders = useMemo(
    () => [
      {
        name: t("landing.about.team.founders.founder5.name"),
        role: t("landing.about.team.founders.founder5.role"),
        description: t("landing.about.team.founders.founder5.description"),
        image: founder5Img,
        imageAlt: t("landing.about.team.founders.founder5.imageAlt"),
        linkedin: t(
          "landing.about.team.founders.founder5.linkedin",
          "https://www.linkedin.com/in/placeholder5",
        ),
      },
      {
        name: t("landing.about.team.founders.founder2.name"),
        role: t("landing.about.team.founders.founder2.role"),
        description: t("landing.about.team.founders.founder2.description"),
        image: founder2Img,
        imageAlt: t("landing.about.team.founders.founder2.imageAlt"),
        linkedin: t(
          "landing.about.team.founders.founder2.linkedin",
          "https://www.linkedin.com/in/placeholder2",
        ),
      },
      {
        name: t("landing.about.team.founders.founder1.name"),
        role: t("landing.about.team.founders.founder1.role"),
        description: t("landing.about.team.founders.founder1.description"),
        image: founder1Img,
        imageAlt: t("landing.about.team.founders.founder1.imageAlt"),
        linkedin: t(
          "landing.about.team.founders.founder1.linkedin",
          "https://www.linkedin.com/in/placeholder1",
        ),
      },
      {
        name: t("landing.about.team.founders.founder4.name"),
        role: t("landing.about.team.founders.founder4.role"),
        description: t("landing.about.team.founders.founder4.description"),
        image: founder4Img,
        imageAlt: t("landing.about.team.founders.founder4.imageAlt"),
        linkedin: t(
          "landing.about.team.founders.founder4.linkedin",
          "https://www.linkedin.com/in/placeholder4",
        ),
      },
      {
        name: t("landing.about.team.founders.founder3.name"),
        role: t("landing.about.team.founders.founder3.role"),
        description: t("landing.about.team.founders.founder3.description"),
        image: founder3Img,
        imageAlt: t("landing.about.team.founders.founder3.imageAlt"),
        linkedin: t(
          "landing.about.team.founders.founder3.linkedin",
          "https://www.linkedin.com/in/placeholder3",
        ),
      },
    ],
    [t],
  );

  // Values data
  const values = useMemo(
    () => [
      {
        icon: faHeart,
        title: t("landing.about.story.values.items.value1.title"),
        description: t("landing.about.story.values.items.value1.description"),
      },
      {
        icon: faLightbulb,
        title: t("landing.about.story.values.items.value2.title"),
        description: t("landing.about.story.values.items.value2.description"),
      },
      {
        icon: faRocket,
        title: t("landing.about.story.values.items.value3.title"),
        description: t("landing.about.story.values.items.value3.description"),
      },
    ],
    [t],
  );

  // Story items data - Histoire (origin) first, then Mission, then Vision
  const storyItems = useMemo(
    () => [
      {
        icon: faSeedling,
        title: t("landing.about.story.origin.title"),
        description: t("landing.about.story.origin.content"),
      },
      {
        icon: faBullseye,
        title: t("landing.about.story.mission.title"),
        description: t("landing.about.story.mission.content"),
      },
      {
        icon: faEye,
        title: t("landing.about.story.vision.title"),
        description: t("landing.about.story.vision.content"),
      },
    ],
    [t],
  );

  return (
    <div className="landing-about">
      {/* Parallax gradient accents */}
      <div
        className="landing-about__gradient-1"
        style={{ transform: `translateY(${scrollY * 0.15}px)` }}
      />
      <div
        className="landing-about__gradient-2"
        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
      />

      {/* Skip to main content for accessibility */}
      <a href="#main-content" className="landing-about__skip-link">
        {t("common.skipToContent", "Skip to main content")}
      </a>

      <LandingHeader />

      {/* Hero Section - Dark theme */}
      <section
        className="landing-hero-section landing-about__hero"
        id="main-content"
      >
        {/* Decorative bubble dots */}
        <div className="landing-about__hero-dots">
          {Array.from({ length: 40 }).map((_, i) => (
            <div
              key={i}
              className={`landing-about__hero-dot landing-about__hero-dot--${i % 8}`}
              style={{
                left: `${5 + ((i * 11) % 90)}%`,
                top: `${8 + ((i * 13) % 85)}%`,
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </div>
        <div className="landing-about__hero-content">
          <div className="landing-about__hero-text">
            <span className="landing-about__tagline">
              {t("landing.about.hero.tagline")}
            </span>
            <h1 className="landing-about__headline">
              {t("landing.about.hero.headline")}
            </h1>
            <p className="landing-about__subheadline">
              {t("landing.about.hero.subheadline")}
            </p>
          </div>

          {/* Accordion */}
          <div className="landing-about__accordion">
            {storyItems.map((item, index) => {
              const isOpen = openAccordionIndex === index;
              return (
                <Collapse
                  key={index}
                  title={
                    <>
                      <div className="landing-about__accordion-icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <span>{item.title}</span>
                    </>
                  }
                  isOpen={isOpen}
                  onToggle={() => {
                    // Handle accordion behavior: only one open at a time
                    setOpenAccordionIndex(isOpen ? null : index);
                  }}
                  className="landing-about__accordion-item"
                  variant="primary"
                >
                  <p className="landing-about__accordion-description">
                    {item.description}
                  </p>
                </Collapse>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="landing-section landing-about__values">
        <div className="landing-about__section-container">
          <h2 className="landing-section-title landing-about__section-title">
            {t("landing.about.story.values.title")}
          </h2>
          <p className="landing-section-subtitle landing-about__section-subtitle">
            {t("landing.about.story.values.subtitle")}
          </p>

          <div className="landing-about__values-grid">
            {values.map((value, index) => (
              <div key={index} className="landing-about__value-card">
                <div className="landing-about__value-icon">
                  <FontAwesomeIcon icon={value.icon} />
                </div>
                <div className="landing-about__value-content">
                  <h4 className="landing-about__value-title">{value.title}</h4>
                  <p className="landing-about__value-description">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="landing-section landing-about__team">
        <div className="landing-about__section-container">
          <h2 className="landing-section-title landing-about__section-title">
            {t("landing.about.team.title")}
          </h2>
          <p className="landing-section-subtitle landing-about__section-subtitle">
            {t("landing.about.team.subtitle")}
          </p>

          <div className="landing-about__team-semicircle">
            {/* Center content area */}
            <div className="landing-about__team-center">
              {selectedFounderIndex !== null ? (
                <div className="landing-about__team-center-content">
                  <h3 className="landing-about__team-center-name">
                    {founders[selectedFounderIndex].name}
                  </h3>
                  <p className="landing-about__team-center-role">
                    {founders[selectedFounderIndex].role}
                  </p>
                  <p className="landing-about__team-center-description">
                    {founders[selectedFounderIndex].description}
                  </p>
                  <a
                    href={founders[selectedFounderIndex].linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="landing-about__team-center-linkedin"
                    aria-label={`${founders[selectedFounderIndex].name} LinkedIn`}
                  >
                    <FontAwesomeIcon icon={faLinkedinIn} />
                  </a>
                </div>
              ) : (
                <div className="landing-about__team-center-placeholder">
                  <p>
                    {t(
                      "landing.about.team.hoverHint",
                      "Hover or click on a founder to learn more",
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Semi-circular avatar arrangement */}
            <div className="landing-about__team-avatars">
              {founders.map((founder, index) => {
                const isSelected = selectedFounderIndex === index;

                // Calculate position on semi-circle (frown/arch shape at bottom)
                const totalAvatars = founders.length;
                const angleRange = 180; // Semi-circle in degrees
                const angleStep =
                  totalAvatars > 1 ? angleRange / (totalAvatars - 1) : 0;
                const angle = 0 + index * angleStep; // Start from 0 (right) to 180 (left) for bottom arc

                // Responsive radius based on viewport
                const getRadius = () => {
                  if (windowWidth >= 1024) return 300; // laptop
                  if (windowWidth >= 768) return 250; // tablet
                  return 200; // mobile
                };

                const baseRadius = getRadius();

                // Convert angle to radians for calculations
                // For bottom arc (frown), we use positive Y (downward)
                // Always use baseRadius for positioning to keep avatars in place
                const angleRad = (angle * Math.PI) / 180;
                const x = Math.cos(angleRad) * baseRadius;
                const y = Math.sin(angleRad) * baseRadius; // Positive for downward (bottom arc)

                // Adjust y offset to move avatars higher on the page
                const yOffset =
                  windowWidth >= 1024 ? 150 : windowWidth >= 768 ? 120 : 90;
                const adjustedY = y - yOffset;

                return (
                  <div
                    key={index}
                    className={`landing-about__team-avatar-wrapper ${
                      isSelected
                        ? "landing-about__team-avatar-wrapper--selected"
                        : ""
                    }`}
                    style={{
                      transform: `translate(calc(-50% + ${x}px), ${adjustedY}px)`,
                    }}
                    onMouseEnter={() => setSelectedFounderIndex(index)}
                    onClick={() =>
                      setSelectedFounderIndex(isSelected ? null : index)
                    }
                  >
                    <UserAvatar
                      src={founder.image}
                      initiales={getInitials(founder.name)}
                      size="120"
                      className="landing-about__team-avatar"
                    />
                    <div className="landing-about__team-avatar-name">
                      {founder.name}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}

export default LandingAbout;
